import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Heart, Sparkles, ChevronsLeftRight } from "lucide-react";
import { money, cdnImg, cdnSrcSet } from "@/lib/api";
import { swatchBg, isLightColor } from "@/lib/colors";
import { useWishlist } from "@/context/WishlistContext";
import { TryOnModal } from "@/components/TryOnModal";

const CARD_SIZES = "(max-width: 640px) 50vw, (max-width: 1024px) 45vw, 30vw";

export const ProductCard = ({ product, index = 0, onAdd, newBadge = false }) => {
  const onSale = product.compare_at && product.compare_at > product.price;
  const { has, toggle } = useWishlist();
  const wished = has(product.slug);
  const [tryOn, setTryOn] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const mainImgRef = useRef(null);
  const [canHover, setCanHover] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );
  const gallery = product.images.slice(0, 5);
  const [imgIdx, setImgIdx] = useState(0);
  const [hintVisible, setHintVisible] = useState(true);
  const touchRef = useRef({ x: 0, y: 0, swiped: false });
  useEffect(() => {
    setCanHover(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
    if (mainImgRef.current?.complete) setImgLoaded(true);
  }, []);
  useEffect(() => {
    if (canHover || gallery.length < 2) return;
    const t = setTimeout(() => setHintVisible(false), 3500);
    return () => clearTimeout(t);
  }, [canHover, gallery.length]);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, swiped: false };
  };
  const onTouchMove = (e) => {
    if (canHover || gallery.length < 2) return;
    const t = e.touches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (!touchRef.current.swiped && Math.abs(dx) > 24 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      touchRef.current.swiped = true;
      setImgIdx((i) => (dx < 0 ? (i + 1) % gallery.length : (i - 1 + gallery.length) % gallery.length));
      setHintVisible(false);
    }
  };
  const onLinkClick = (e) => {
    if (touchRef.current.swiped) {
      e.preventDefault();
      touchRef.current.swiped = false;
    }
  };
  const simple = !(product.sizes?.length) && !(product.colors?.length);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col"
      data-testid={`product-card-${product.slug}`}
    >
      <Link
        to={`/product/${product.slug}`}
        className="block relative overflow-hidden bg-[#EFEFEA] aspect-[2/3]"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onClick={onLinkClick}
      >
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">          {newBadge && (
            <span className="bg-[#2F5D3A] text-white text-[0.55rem] font-mono tracking-[0.12em] uppercase px-2 py-1">
              New In
            </span>
          )}
          {onSale && (
            <span className="bg-[#B84A3B] text-white text-[0.6rem] font-mono font-medium tracking-[0.08em] px-2 py-1">
              {product.discount_pct}% OFF
            </span>
          )}
          {product.badge && (
            <span className="bg-[#1A1A1A] text-[#FFFFFF] text-[0.55rem] font-mono tracking-[0.12em] uppercase px-2 py-1">
              {product.badge}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
          <button
            data-testid={`wishlist-toggle-${product.slug}`}
            onClick={(e) => { e.preventDefault(); toggle(product); }}
            aria-label="Save to wishlist"
            className="w-8 h-8 rounded-full bg-[#FFFFFF]/85 backdrop-blur-sm flex items-center justify-center hover:bg-[#FFFFFF] transition-colors"
          >
            <Heart size={15} className={wished ? "text-[#B84A3B]" : "text-[#1A1A1A]"} fill={wished ? "#B84A3B" : "none"} strokeWidth={1.8} />
          </button>
          <button
            data-testid={`tryon-card-${product.slug}`}
            onClick={(e) => { e.preventDefault(); setTryOn(true); }}
            aria-label="Try it on virtually"
            title="Try it on"
            className="w-8 h-8 rounded-full bg-[#FFFFFF]/85 backdrop-blur-sm flex items-center justify-center hover:bg-[#FFFFFF] transition-colors"
          >
            <Sparkles size={15} className="text-[#B84A3B]" strokeWidth={1.8} />
          </button>
        </div>
        <img
          src={cdnImg(product.images[0], 24)}
          aria-hidden="true"
          alt=""
          className={`absolute inset-0 w-full h-full object-cover scale-110 blur-lg transition-opacity duration-700 ${imgLoaded ? "opacity-0" : "opacity-100"}`}
        />
        <img
          ref={mainImgRef}
          src={cdnImg(gallery[imgIdx], 800)}
          srcSet={cdnSrcSet(gallery[imgIdx])}
          sizes={CARD_SIZES}
          alt={product.title}
          loading="lazy"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          className={`relative w-full h-full object-cover transition-[transform,opacity] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] ${imgLoaded ? "opacity-100" : "opacity-0"}`}
        />
        {canHover && product.images[1] && (
          <img
            src={cdnImg(product.images[1], 800)}
            srcSet={cdnSrcSet(product.images[1])}
            sizes={CARD_SIZES}
            alt={product.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 group-hover:scale-[1.06] transition-[transform,opacity] duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          />
        )}
        {!canHover && gallery.length > 1 && (
          <div
            data-testid={`card-dots-${product.slug}`}
            className="absolute bottom-2.5 inset-x-0 z-10 flex justify-center gap-1.5 pointer-events-none"
          >
            {gallery.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${i === imgIdx ? "w-3.5 bg-[#FFFFFF]" : "w-1 bg-[#FFFFFF]/60"}`}
              />
            ))}
          </div>
        )}
        {!canHover && gallery.length > 1 && hintVisible && imgIdx === 0 && (
          <div
            data-testid={`swipe-hint-${product.slug}`}
            className="absolute bottom-7 inset-x-0 z-10 flex justify-center pointer-events-none"
          >
            <span className="flex items-center gap-1.5 rounded-full bg-[#1A1A1A]/70 backdrop-blur-sm text-[#FFFFFF] text-[0.55rem] font-mono tracking-[0.12em] uppercase px-2.5 py-1 animate-pulse">
              <ChevronsLeftRight size={11} strokeWidth={2} />
              Swipe for more
            </span>
          </div>
        )}
        {product.sizes?.length > 0 && !(product.colors?.length > 0) && (
          <div className="absolute bottom-0 inset-x-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-[#FFFFFF]/95 backdrop-blur-sm px-3 py-2.5 flex flex-wrap justify-center gap-1.5">
            {product.sizes.map((s) => (
              <button
                key={s}
                data-testid={`card-size-${product.slug}-${s}`}
                onClick={(e) => { e.preventDefault(); onAdd?.(product, s); }}
                className="min-w-[1.9rem] px-1.5 h-7 border border-[#1A1A1A] text-[0.6rem] font-mono tracking-wider hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors duration-200"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </Link>

      <div className="pt-3 flex flex-col">
        {product.reviews_count > 0 && (
          <div className="flex items-center gap-1 mb-1">
            <div className="flex text-[#B84A3B]">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={11} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <span className="text-[0.62rem] font-mono text-[#6E6E69]">({product.reviews_count})</span>
          </div>
        )}
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-body text-[0.85rem] leading-snug text-[#1A1A1A] group-hover:text-[#B84A3B] transition-colors duration-300 line-clamp-1">
            {product.title}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="font-body font-semibold text-sm text-[#1A1A1A]">{money(product.price)}</span>
          {onSale && (
            <>
              <span className="font-body text-xs text-[#B0B0A8] line-through">{money(product.compare_at)}</span>
            </>
          )}
        </div>
        {product.reviews_count > 0 && (
          <p className="flex items-center gap-1 mt-1.5" data-testid={`card-rating-${product.slug}`}>
            <span className="text-[#B84A3B] text-xs tracking-tight">{"★".repeat(Math.round(product.rating))}<span className="text-[#B0B0A8]">{"★".repeat(5 - Math.round(product.rating))}</span></span>
            <span className="text-[0.65rem] font-mono text-[#6E6E69]">{product.rating} ({product.reviews_count})</span>
          </p>
        )}
        {product.colors?.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2" data-testid={`card-colors-${product.slug}`}>
            {product.colors.slice(0, 5).map((c) => (
              <span key={c} title={c}
                className={`w-3.5 h-3.5 rounded-full border ${isLightColor(c) ? "border-[#1A1A1A]/25" : "border-black/15"}`}
                style={{ background: swatchBg(c) }} />
            ))}
            {product.colors.length > 5 && (
              <span className="text-[0.6rem] font-mono text-[#6E6E69] ml-0.5">+{product.colors.length - 5}</span>
            )}
          </div>
        )}
        {product.sizes?.length || product.colors?.length ? (
          <Link
            to={`/product/${product.slug}`}
            data-testid={`add-cart-${product.slug}`}
            className="mt-3 block w-full text-center border border-[#1A1A1A] text-[0.62rem] font-mono tracking-[0.2em] uppercase py-2.5 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors duration-300"
          >
            Select Options
          </Link>
        ) : (
          <button
            data-testid={`add-cart-${product.slug}`}
            onClick={() => onAdd?.(product, null)}
            className="mt-3 w-full border border-[#1A1A1A] text-[0.62rem] font-mono tracking-[0.2em] uppercase py-2.5 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors duration-300"
          >
            Add to Cart
          </button>
        )}
      </div>

      <TryOnModal
        open={tryOn}
        onOpenChange={setTryOn}
        product={product}
        onAddToCart={simple ? () => onAdd?.(product, null) : undefined}
      />
    </motion.div>
  );
};
